// GT1 (register §42, D-GT1/D-GT2): the ONE gate catalog. Every human-in-the-
// loop step is an ID-bearing gate (SPINE pw-1372-inv-01) decoded against the
// estate's tested vocabulary — Projects/harmony-process/outcome-contract/
// schemas/outcome-contract.schema.json $defs.humanGate, with estate identity,
// decider, entitlement and inline card-wait metadata. cardWaitMs is a call
// wait policy, never a stored gate expiry or permission.
// "Autonomous" is not a kind: auto-pass-with-receipt is the far end of
// HUMAN_ON_THE_LOOP (pw-0137-inv-01). This module is the only home; the web
// derives from the door (neucleos_read_gate_catalog), never a second list.

export const GATE_KINDS = Object.freeze([
  "HUMAN_IN_THE_LOOP",
  "HUMAN_ON_THE_LOOP",
  "HUMAN_OBSERVATION",
  "HUMAN_INPUT",
]);
export const GATE_TIMINGS = Object.freeze(["BEFORE_EFFECT", "DURING_EFFECT", "AFTER_EFFECT"]);
// A = licensed-attorney judgment, C = client / authorized representative,
// A+C = both, D = dual control, O = operational owner, N = no mandatory
// approval (automated with exception escalation and receipts).
export const DECIDER_CLASSES = Object.freeze(["A", "C", "A+C", "D", "O", "N"]);
export const HUMAN_DECIDER_CLASSES = Object.freeze(["A", "C", "A+C", "D", "O"]);
// Kinds that hold the effect until a human decides (BEFORE_EFFECT).
export const BLOCKING_KINDS = Object.freeze(["HUMAN_IN_THE_LOOP", "HUMAN_INPUT"]);

// Provisional capability for attorney-side legal gates — the principal
// confirms the name (the commerce.operate precedent, §35 D-SB6). Until it is
// seated on an Actor the gates it entitles appear in no one's table (D-GT5).
export const LEGAL_PRACTICE_CAPABILITY = "legal.practice";
export const LEGAL_ROUTE_CAPABILITY = "legal.route";

const legalView = (fields) => ({ kind: "preview", mustShow: fields });
const legalSurface = "nco:surface/cockpit.route-preview";

function legalGate(gateKey, title, opts) {
  return Object.freeze({
    "@id": `nco:gate/${gateKey}`,
    "@type": "nco:HumanGate",
    gateKey,
    title,
    kind: "HUMAN_IN_THE_LOOP",
    timing: "BEFORE_EFFECT",
    initialState: "CREATED",
    defaultOutcome: "blocked",
    delegation: { humanDelegation: opts.humanDelegation, cleoAugmentation: opts.cleoAugmentation },
    changeControl: { worldChangeable: opts.worldChangeable ?? false, userChangeable: opts.userChangeable ?? false },
    requiredView: legalView(opts.mustShow),
    allowedOutcomes: opts.allowedOutcomes,
    satisfyingOutcomes: opts.satisfyingOutcomes,
    eligibleSurfaceBindings: [legalSurface],
    deciderClass: opts.deciderClass,
    // The Business Counsel arc re-articulates these onto its minted Cans when
    // it opens (register §42 named absence); the stage id is the README's.
    cleoCanRef: opts.cleoCanRef,
    capability: opts.capability,
    graduable: opts.graduable ?? false,
  });
}

// US arc (register §41, settings session): the settings gate family. The
// membership floor is mywords.read (every provisioned member holds it), so
// these rows appear in every member's table (D-GT5).
export const MEMBERSHIP_FLOOR_CAPABILITY = "mywords.read";
const settingsSurface = "nco:surface/desk.settings-v2";
function settingsGate(gateKey, title, opts) {
  return Object.freeze({
    "@id": `nco:gate/${gateKey}`,
    "@type": "nco:HumanGate",
    gateKey,
    title,
    kind: opts.kind ?? "HUMAN_IN_THE_LOOP",
    timing: opts.timing ?? "BEFORE_EFFECT",
    initialState: "CREATED",
    defaultOutcome: opts.defaultOutcome ?? "blocked",
    delegation: { humanDelegation: opts.humanDelegation ?? "delegable", cleoAugmentation: opts.cleoAugmentation ?? "prepare-preview" },
    changeControl: { worldChangeable: opts.worldChangeable ?? false, userChangeable: opts.userChangeable ?? true },
    requiredView: { kind: "preview", mustShow: opts.mustShow },
    allowedOutcomes: opts.allowedOutcomes ?? ["approved", "declined"],
    satisfyingOutcomes: opts.satisfyingOutcomes ?? ["approved"],
    eligibleSurfaceBindings: [settingsSurface],
    deciderClass: opts.deciderClass ?? "C",
    cleoCanRef: opts.cleoCanRef,
    capability: opts.capability ?? MEMBERSHIP_FLOOR_CAPABILITY,
    graduable: opts.graduable ?? true,
  });
}

// GT7 (register §42, GT-CAN-07): the Cleo Code tool-family gates. The four
// runtime modes are a PROJECTION onto these defaults (apps/server toolGates);
// a governed thread's standing overrides the projection. File reads are
// observation only — looking writes nothing.
const toolSurface = "nco:surface/desk.composer";
function toolGate(gateKey, title, opts) {
  return Object.freeze({
    "@id": `nco:gate/${gateKey}`,
    "@type": "nco:HumanGate",
    gateKey,
    title,
    kind: opts.kind ?? "HUMAN_IN_THE_LOOP",
    timing: opts.timing ?? "BEFORE_EFFECT",
    initialState: "CREATED",
    defaultOutcome: opts.defaultOutcome ?? "blocked",
    delegation: { humanDelegation: opts.humanDelegation ?? "delegable", cleoAugmentation: "summarize-request" },
    changeControl: { worldChangeable: true, userChangeable: opts.userChangeable ?? true },
    requiredView: { kind: "preview", mustShow: ["tool-name", "input-summary", "cwd"] },
    allowedOutcomes: opts.allowedOutcomes ?? ["approved", "declined"],
    satisfyingOutcomes: opts.satisfyingOutcomes ?? ["approved"],
    eligibleSurfaceBindings: [toolSurface],
    deciderClass: opts.deciderClass ?? "C",
    cleoCanRef: "GT-CAN-07",
    capability: MEMBERSHIP_FLOOR_CAPABILITY,
    graduable: opts.graduable ?? true,
  });
}
export const TOOL_FAMILY_GATE_KEYS = Object.freeze({
  file_change_approval: "HG-TOOL-FILE-CHANGE-01",
  command_execution_approval: "HG-TOOL-COMMAND-01",
  dynamic_tool_call: "HG-TOOL-DYNAMIC-01",
  file_read_approval: "HO-TOOL-FILE-READ-01",
});

// CHR10 / GT1 (GOALS-HITL.html#declaration-and-delivery, #authorization-
// contract): the operation gates every reviewed process declaration binds
// to. One gate ID per decision contract (participant, trigger, required
// view); an operation reuses a gate only when its contract is the same.
// HUMAN_INPUT: the person's own authenticated request is the decision;
// Cleo's own initiative still awaits the person (D-GT14).
const operationSurface = "nco:surface/desk.composer";
function operationGate(gateKey, title, opts) {
  return Object.freeze({
    "@id": `nco:gate/${gateKey}`,
    "@type": "nco:HumanGate",
    gateKey,
    title,
    kind: opts.kind ?? "HUMAN_IN_THE_LOOP",
    timing: opts.timing ?? "BEFORE_EFFECT",
    initialState: "CREATED",
    defaultOutcome: opts.defaultOutcome ?? "blocked",
    delegation: { humanDelegation: opts.humanDelegation ?? "delegable", cleoAugmentation: opts.cleoAugmentation ?? "prepare-preview" },
    changeControl: { worldChangeable: opts.worldChangeable ?? true, userChangeable: opts.userChangeable ?? true },
    requiredView: { kind: "preview", mustShow: opts.mustShow },
    allowedOutcomes: opts.allowedOutcomes ?? ["approved", "declined"],
    satisfyingOutcomes: opts.satisfyingOutcomes ?? ["approved"],
    eligibleSurfaceBindings: [operationSurface],
    deciderClass: opts.deciderClass ?? "C",
    cleoCanRef: opts.cleoCanRef ?? "CHR10",
    capability: opts.capability ?? MEMBERSHIP_FLOOR_CAPABILITY,
    // Q5 (PR F): further capabilities whose holders also see this gate in their own table (lib/gateStanding.js
    // resolveEntitledGates, gates.js gatesForSubject). `capability` stays the gate's one named capability.
    ...(opts.tableCapabilities ? { tableCapabilities: Object.freeze([...opts.tableCapabilities]) } : {}),
    ...(opts.cardWaitMs !== undefined ? { cardWaitMs: opts.cardWaitMs } : {}),
    graduable: opts.graduable ?? true,
  });
}
// Gates approved only through the person's hardware-presence credential
// (Touch ID) bound to the exact call (requireExactEffectApprovalCredential):
// a host bearer, connector or Cleo reaches the same decide door as the Desk,
// so the door alone cannot tell a person's click from a program's. Declining
// stays open to every door.
export const PRESENCE_APPROVED_GATE_KEYS = Object.freeze(new Set(["HG-COMMERCE-OPERATE-02"]));
// Gates approved only on a card that shows the stored change the call applies (Can 68 step 3: a change to what
// Neucleos charges is approved on the card listing every new price). A Desk that shows the card says so on every
// World request (x-neucleos-desk-capabilities, lib/canvasProtocol.js deskCapabilitiesOf). For a caller that does not
// (an older Desk, which would show only the change's key and digest, the phone, a connector), the call is refused
// before anyone is asked, no approval for it is opened, and none is approved; declining stays open to every door.
// The declaration only ever refuses: it permits nothing the gate does not. Gate key -> the card's capability.
// v1: the switch-over's card (Desk 0.0.187). v2: any stored kind from its title and lines, but 0.0.187 also adds the
// switch-over's own "new work takes these prices" row to every card, which is wrong for a route (neucleos-06's AG15
// check, 5 October 2026), so nothing requires v2. v3: any stored kind from its title and lines, with wording of its own
// kind only (Desk 0.0.188 on). The capability says what the Desk actually renders (the Orchestrator, 5 October 2026).
export const COMMERCIAL_CHANGE_CARD_CAPABILITY = "commercial-change-card-v1";
export const COMMERCIAL_CHANGE_CARD_V2_CAPABILITY = "commercial-change-card-v2";
export const COMMERCIAL_CHANGE_CARD_V3_CAPABILITY = "commercial-change-card-v3";
// Can 73 (docs/world-members-design.md §5.3): "Change who belongs to this World" is approved only on the Members card,
// which shows the stored change's lines and, for an email, its exact message (world-member-change-v1).
export const WORLD_MEMBERS_CARD_CAPABILITY = "world-members-card-v1";
// Can 81 (the coding-jobs plan, 17 October 2026): "Start a coding job that changes a repository" is approved only on the
// coding job card, which shows the repository, the whole brief and the time limit, built from the call itself.
export const CODING_JOB_CARD_CAPABILITY = "coding-job-card-v1";
// The principal's pause exception of 8 October 2026, item (4): publishing several pages of a website at once
// (neucleos_canvas canvas-public-publish-pages) is approved only on the website pages card, which shows each path
// as changed (exactly what is removed and added), added or kept. Keyed by the STEP, not the gate: HG-PUBLISH-01
// also decides single-page publication, friend links and offers, which keep their own cards.
export const WEBSITE_PAGES_CARD_CAPABILITY = "website-pages-card-v1";
// Step 3 K2c2 (the Orchestrator's K2 GO, 9 October 2026): publishing a platform Components release
// (neucleos_canvas component-release-publish) is approved only on the platform release card: each Component added,
// changed or kept by name, the keep-the-old-version line and the terms, exactly as component-release-review answers
// it from the stored release (platformComponentReleases.js releaseCard). Keyed by the STEP: HG-PUBLISH-01 also
// decides every other publication, which keep their own cards.
export const COMPONENT_RELEASE_CARD_CAPABILITY = "component-release-card-v1";
export const WEBSITE_PAGES_STEP_KEY = "canvas-creation-and-revision/publish-website-pages";
// Step 3 I2: a live summary of a collection on a website (neucleos_canvas dataset-aggregate-create) is approved only on
// the aggregate dataset card: the metric, field, group and filter in plain words, and that it covers EVERY matching
// record up to 1000, built from the stored call (web aggregateDatasetCard.ts). Keyed by the STEP.
export const AGGREGATE_DATASET_CARD_CAPABILITY = "aggregate-dataset-card-v1";
export const AGGREGATE_DATASET_STEP_KEY = "canvas-creation-and-revision/publish-aggregate-dataset";
export const COMPONENT_RELEASE_STEP_KEY = "canvas-creation-and-revision/publish-component-release";
// One-backend plan step 3, 4b-ii: a helper started from a conversation kept in the World (neucleos_start_own_session) is
// approved only on the World helper card (@cleo/shared worldHelperCard): its name, its first message exactly, and that it
// runs on the World's Standard tier, paid from World credits, never the person's own plan. Keyed by the STEP. The Desk's
// pane shows it; a screen that does not (Pocket before its update, an older Desk) may only decline it.
export const WORLD_HELPER_CARD_CAPABILITY = "world-helper-card-v1";
export const WORLD_HELPER_STEP_KEY = "cloud-helper-start/world-helper-start";
// It spends World credits, so the person decides each helper on its card, every time (lib/gatePolicy.js readGateLawEntry):
// a delegation of HG-SESSION-DISPATCH-01 to Cleo stays recorded and does not apply to it, and no World or personal setting
// of that gate loosens it so the card is skipped (Codex reviews of #2401).
export const ASKED_EVERY_TIME_STEP_KEYS = Object.freeze(new Set([WORLD_HELPER_STEP_KEY]));
export const CARD_APPROVED_STEP_KEYS = Object.freeze(new Map([
  [WORLD_HELPER_STEP_KEY, WORLD_HELPER_CARD_CAPABILITY],
  [WEBSITE_PAGES_STEP_KEY, WEBSITE_PAGES_CARD_CAPABILITY],
  [COMPONENT_RELEASE_STEP_KEY, COMPONENT_RELEASE_CARD_CAPABILITY],
  [AGGREGATE_DATASET_STEP_KEY, AGGREGATE_DATASET_CARD_CAPABILITY],
]));
/**
 * Can 47: the cards Pocket shows exactly as the Desk's card does, so an approval needing one may be approved on Pocket's card
 * in the conversation (pocketCloudDoors.decideGate shownCard), in the same words (@cleo/shared). The coding job and aggregate
 * dataset cards are built only from the call's own arguments (codingJobCard, aggregateDatasetCard); the Members card shows the
 * stored change the World hands Pocket with the approval (memberChangeCard; pocketCloudDoors.waitingApprovals memberChange), and so
 * does a price change's card (commercialChangeCard; waitingApprovals commercialChange).
 * Any other card-only approval is declined only, in Pocket.
 */
export const POCKET_SHOWN_CARDS = Object.freeze([CODING_JOB_CARD_CAPABILITY, AGGREGATE_DATASET_CARD_CAPABILITY, WORLD_MEMBERS_CARD_CAPABILITY,
  // The World helper card (4b-ii): the Desk's pane shows it from the call's own arguments; a phone without it declines only.
  WORLD_HELPER_CARD_CAPABILITY,
  // A price change's card, the switch-over's (v1) and any other kind's (v3), from the stored change (commercialChangeCard).
  COMMERCIAL_CHANGE_CARD_CAPABILITY, COMMERCIAL_CHANGE_CARD_V3_CAPABILITY]);
/** The card an approval needs (its gate's, or its step's), or null when none. */
export const cardNeededFor = (gateKey, stepKey) => missingApprovalCard(gateKey, [], undefined, stepKey);
/** Whether a gate instance (its gate, and its step when it has one) is approved only on its card. */
export const cardApprovedFor = (gateKey, stepKey) => CARD_APPROVED_GATE_KEYS.has(gateKey) || (typeof stepKey === "string" && CARD_APPROVED_STEP_KEYS.has(stepKey));
export const CARD_APPROVED_GATE_KEYS = Object.freeze(new Map([
  ["HG-COMMERCE-PRICE-01", COMMERCIAL_CHANGE_CARD_CAPABILITY],
  ["HG-WORLD-GOVERN-02", WORLD_MEMBERS_CARD_CAPABILITY],
  ["HG-CODE-JOB-01", CODING_JOB_CARD_CAPABILITY],
]));
// Audit C3 (#1330): the Desk's own server refuses an approval of a card-approved gate from anywhere but its card
// (Pocket, the phone, a bound session, Cleo's own accept). A Desk built before that (5-6 October) declares the cards
// above yet relays those approvals, so APPROVING such a gate also needs this declaration. Asking, opening, showing and
// declining do not: an older Desk still shows the approval and declines it, and the same approval, still waiting, is
// approved once Cleo on the Desk is updated (lib/worldAuth.js refuseDecision). It only ever refuses.
export const CARD_APPROVED_GATES_CAPABILITY = "card-approved-gates-v1";
export const CARD_CAPABILITIES = Object.freeze([WORLD_HELPER_CARD_CAPABILITY, COMMERCIAL_CHANGE_CARD_CAPABILITY, COMMERCIAL_CHANGE_CARD_V2_CAPABILITY, COMMERCIAL_CHANGE_CARD_V3_CAPABILITY, WORLD_MEMBERS_CARD_CAPABILITY, CODING_JOB_CARD_CAPABILITY, CARD_APPROVED_GATES_CAPABILITY, WEBSITE_PAGES_CARD_CAPABILITY, COMPONENT_RELEASE_CARD_CAPABILITY, AGGREGATE_DATASET_CARD_CAPABILITY]);
// PR #1721 (W-89a78759, after #1708): this Desk stamps the person-authored mark (lib/personAuthorship.js) on a typed
// composer Send, so the World can tell the person's own typed request from any other Send the Desk attests (a Slack
// relay, a Steer, a scripted or bot Send). The mark proves the person's signed-in session, not the Desk's composer: a
// client holding that session can record one too (W-93020153, a recorded residual; it can approve the same cards anyway). W-2ef5f6d7: the World now requires the mark of every caller, so a request naming
// a conversation decides a HUMAN_INPUT step only when that message carries the mark (lib/worldAuth.js openGateInstance), and
// an unmarked one asks the person on a card, whatever the call declares. The header is still sent (Desk 0.0.201 and after)
// and still parsed (lib/canvasProtocol.js); no decision reads it.
export const PERSON_MARK_CAPABILITY = "person-mark-v1";
// W-5fff7990 (design revision 2 and the lead's build decision after Codex r2, 20 Oct 2026): a Desk older than 0.0.191
// sends Cleo's presence, read-receipt default and profile changes with no thread, which the World cannot tell from the
// person's own Settings. The Desk's three person-only Settings routes (commsHttp.ts set-receipts-default and
// presence/visibility, worldDoorHttp.ts profile/save) mark their own World request with this name, and nothing else on the
// Desk sends it: not set_setting's fallback, not the photo, not any other call. It says "this request came from the
// person's own Settings route"; it does not stop a client that forges it with the person's own credential.
export const PERSON_SETTINGS_MARKER = "person-settings-v1";
// The tools those three routes write are PERSON_SETTINGS_TOOLS in lib/cleoActionLists.js (tool lists live beside the registry).
// The switch, a Convex deployment setting read at each call, never cached (the COST_CARDS pattern, costChange.js): only
// "on" turns it on, and unset is off. While it is off the World writes these as before. When on, an unmarked unthreaded
// host change of these three is refused (toolCallGates.requireStepsInEffect): a Desk that does not send the mark (every
// Desk before the release that adds it) can no longer change them from Settings until it is updated. Turning it on is the
// principal's compatibility decision, asked once his Desk and the members' Desks run a build that sends the mark.
export const PERSON_SETTINGS_MARK_SETTING = "REQUIRE_PERSON_SETTINGS_MARK";
export const personSettingsMarkRequired = () => process.env[PERSON_SETTINGS_MARK_SETTING] === "on";
export const PERSON_SETTINGS_UPDATE_CODE = "DESK_UPDATE_NEEDED";
export const PERSON_SETTINGS_UPDATE_REFUSAL = `REFUSED [${PERSON_SETTINGS_UPDATE_CODE}]: This version of Cleo can't change this setting any more. Update Cleo on this Mac, then change it again. Nothing was changed.`;
// Step 3 I2 (the coordinator's ruling (a), 9 Oct 2026; his rule 4): this Desk is built after the Canvas door took #1834's
// rule, so for it a Canvas write Cleo makes from his own typed request in the same message (collection writes, a save
// of a Canvas others can read) is decided by that request (mcpCanvas.js requireCanvasWriteGate). Desks before it
// (0.0.200, 0.0.201) do not say so and keep asking as before. It only ever narrows which Desks the rule reaches.
export const CANVAS_TYPED_REQUEST_CAPABILITY = "canvas-typed-request-v1";
// PR #1834 (the Orchestrator's ruling 1, option ii, 9 Oct 2026; his rule 4, docs/how-cleo-works.md: "One approval per
// outcome, at the moment it goes out (publish, send, spend, delete, connect)"): the HUMAN_INPUT gates the person's own
// request never decides, on any engine. Connecting an outside account, governing the World, the platform or its boards,
// and changing the World's brand each ask on a card even when he typed the request. Read by the World door
// (toolCallGates.js requireStepsForCall: no request is attached for them) and by openGateInstance (lib/worldAuth.js: a
// request present, as the Desk's bridge sends for Claude and Codex, decides nothing for them). It only ever tightens.
// Step 3 I2 (the Orchestrator's condition on ruling (a), 9 Oct 2026: "HG-PUBLISH-01 (public publishing, sharing, audience
// changes) and REQUEST_NEVER_DECIDES always ask"; Codex round 1 P1): HG-PUBLISH-01 joins, so a World that sets a sharing
// step to HUMAN_INPUT still never has it decided by a typed request.
export const REQUEST_NEVER_DECIDES = Object.freeze(new Set([
  "HG-EXTERNAL-CONNECT-01", "HG-WORLD-GOVERN-01", "HG-PLATFORM-GOVERN-01", "HG-BOARD-GOVERN-01", "HG-WORLD-BRAND-01", "HG-PUBLISH-01",
  "HG-AGENT-ASK-01",
]));
/**
 * PR #1834 review round 1 (Codex P2): an approval of one of those gates decided by a request in a conversation (opened
 * before this rule, or by the Desk's bridge) answers no call and is never handed back: a fresh decision on the card is
 * required. The person's own gesture on the Desk (a request naming no conversation) is not such an approval.
 */
export const requestApprovalNeverAnswers = (row) => row?.decider === "self-request" && row.request?.threadId !== undefined
  && REQUEST_NEVER_DECIDES.has(row.gateKey);
/**
 * W-4a8f853f, W-2ef5f6d7: one request-binding rule, shared by the tool-call door's selection of an approval
 * (toolCallGates.js answerStep) and by the reopening of an occurrence's approval (lib/worldAuth.js openGateInstance). An
 * approval the person's own message in a conversation decided (decider self-request, its request names the conversation)
 * is used only by a call whose own request is that same message in that conversation: `request` is the call's own
 * request, already proved a message the person typed (the World's mark), or undefined when the call has none. Any other
 * approval is not bound by a request. A caller that cannot show a typed request of its own (a relay, a Steer, a scripted
 * Send, a caller that does not mark typed Sends) never uses one.
 */
export const requestBindsApproval = (row, request) => !(row?.decider === "self-request" && row.request?.threadId !== undefined)
  || (request !== undefined && request !== null && request.threadId === row.request.threadId && request.messageDigest === row.request.messageDigest);
/** The card a Desk must show for a stored change of this kind: the switch-over's own (v1), or the general one (v3). */
export const cardForChangeKind = (kind) => (kind === "switch-over" ? COMMERCIAL_CHANGE_CARD_CAPABILITY : COMMERCIAL_CHANGE_CARD_V3_CAPABILITY);
/**
 * The card a caller must show for this gate and does not say it shows, else null. `kind`, when the change behind the
 * call is known, raises the gate's card to that kind's (lib/worldAuth.js reads it from the stored change).
 */
export function missingApprovalCard(gateKey, deskCapabilities, kind, stepKey) {
  // A missing or unreadable capability list is an app that shows no card: it lacks the card, never "unknown".
  const base = CARD_APPROVED_GATE_KEYS.get(gateKey) ?? (typeof stepKey === "string" ? CARD_APPROVED_STEP_KEYS.get(stepKey) : undefined);
  // Only a price change's card depends on its kind; the Members card shows every member change (§5.3).
  const card = base && kind !== undefined && gateKey === "HG-COMMERCE-PRICE-01" ? cardForChangeKind(kind) : base;
  return card && !(Array.isArray(deskCapabilities) && deskCapabilities.includes(card)) ? card : null;
}
/**
 * Audit C3 (#1330 round 4): approving a card-approved gate also needs a Desk that says it refuses that approval from
 * anywhere but its card (CARD_APPROVED_GATES_CAPABILITY). The capability it lacks, else null. Only an approval asks
 * this; a decline, and asking or opening, never do. A caller with no Desk header (a cloud conversation, the phone, a
 * connector) declares nothing, so it lacks it too.
 */
export function missingCardOnlyGuard(gateKey, deskCapabilities, stepKey) {
  if (!cardApprovedFor(gateKey, stepKey)) return null;
  return Array.isArray(deskCapabilities) && deskCapabilities.includes(CARD_APPROVED_GATES_CAPABILITY) ? null : CARD_APPROVED_GATES_CAPABILITY;
}
/** What an older Desk is told when its person approves a card-approved gate (refuseDecision). */
export const CARD_ONLY_GUARD_REFUSAL_LEAD = "Update Cleo on the Desk to approve this";
/** The refusal sentence for a change asked of an app that cannot show its card. */
export const APPROVAL_CARD_REFUSAL = "REFUSED [GATE_CARD_NOT_SHOWN]: a change to what Neucleos charges is approved only on the card that lists every new price, and this app cannot show that card. Next legal move: update Cleo on the Desk, then prepare and approve it there. Nobody was asked anything and nothing was written.";
/** Can 73 (§5.3): the Members card's own sentence, for a change to who belongs to a World asked of an app without it. */
export const WORLD_MEMBERS_CARD_REFUSAL = "REFUSED [GATE_CARD_NOT_SHOWN]: a change to who belongs to this World is approved only on the Members card, which shows the person, the World, what changes and what can and cannot be undone, and this app cannot show that card. Next legal move: update Cleo on the Desk, then prepare and approve it there. Nobody was asked anything and nothing was written.";
/** Can 81: the coding job card's own sentence, for a coding job asked of an app without it. */
export const WORLD_HELPER_CARD_REFUSAL = "REFUSED [GATE_CARD_NOT_SHOWN]: a helper session started from here is approved only on its own card, which names it, its first message and that it runs on World credits, and this app cannot show that card. Next legal move: ask again on the Desk, in this same conversation. Nobody was asked anything and no helper was started.";
export const CODING_JOB_CARD_REFUSAL = "REFUSED [GATE_CARD_NOT_SHOWN]: a coding job is approved only on its own card, which shows the repository, the whole brief and the time limit, and this app cannot show that card. Next legal move: update Cleo on the Desk, then ask again there. Nobody was asked anything and no job was started.";
/** The website pages card's own sentence, for a publication of several pages asked of an app without it. */
export const WEBSITE_PAGES_CARD_REFUSAL = "REFUSED [GATE_CARD_NOT_SHOWN]: publishing several pages of a website is approved only on the website pages card, which shows each page as changed (exactly what is removed and added), added or kept, and this app cannot show that card. Next legal move: update Cleo on the Desk, then ask again there. Nobody was asked anything and nothing was published.";
/** The platform release card's own sentence, for publishing a platform Components release asked of an app without it. */
export const COMPONENT_RELEASE_CARD_REFUSAL = "REFUSED [GATE_CARD_NOT_SHOWN]: publishing platform Components to every World's Library is approved only on the platform release card, which names each Component as added, changed or kept and states the terms they are published under, and this app cannot show that card. Next legal move: update Cleo on the Desk, then ask again there. Nobody was asked anything and nothing was published.";
// Each card-only step's own words, chosen by its step key: the refusal at the door (approvalCardRefusal) and the
// World's refusal to open its approval (lib/worldAuth.js openGateInstance).
/** The aggregate dataset card's own sentence, for a live summary asked of an app without it. */
export const AGGREGATE_DATASET_CARD_REFUSAL = "REFUSED [GATE_CARD_NOT_SHOWN]: a live summary of a collection on a website is approved only on its own card, which says what it computes and that it covers every matching record up to 1000, and this app cannot show that card. Next legal move: update Cleo on the Desk, then ask again there. Nobody was asked anything and nothing was published.";
const STEP_CARD_REFUSALS = Object.freeze(new Map([
  [WORLD_HELPER_STEP_KEY, WORLD_HELPER_CARD_REFUSAL],
  [WEBSITE_PAGES_STEP_KEY, WEBSITE_PAGES_CARD_REFUSAL],
  [COMPONENT_RELEASE_STEP_KEY, COMPONENT_RELEASE_CARD_REFUSAL],
  [AGGREGATE_DATASET_STEP_KEY, AGGREGATE_DATASET_CARD_REFUSAL],
]));
export const STEP_CARD_PHRASES = Object.freeze(new Map([
  [WORLD_HELPER_STEP_KEY, "for a helper session started from here is approved only on its own card, which names it, its first message and that it runs on World credits"],
  [WEBSITE_PAGES_STEP_KEY, "for publishing several website pages is approved only on the website pages card, which shows each page as changed, added or kept"],
  [COMPONENT_RELEASE_STEP_KEY, "for publishing a platform Components release is approved only on the platform release card, which names each Component as added, changed or kept and states its terms"],
  [AGGREGATE_DATASET_STEP_KEY, "for a live summary on a website is approved only on its own card, which says what it computes and that it covers every matching record up to 1000"],
]));
/** The refusal sentence for a card-approved gate asked of an app that cannot show its card. */
export const approvalCardRefusal = (gateKey, stepKey) =>
  (typeof stepKey === "string" && STEP_CARD_REFUSALS.get(stepKey)) || (gateKey === "HG-WORLD-GOVERN-02" ? WORLD_MEMBERS_CARD_REFUSAL : gateKey === "HG-CODE-JOB-01" ? CODING_JOB_CARD_REFUSAL : APPROVAL_CARD_REFUSAL);

export const OPERATION_GATE_KEYS = Object.freeze([
  "HG-COMM-SEND-01", "HG-PUBLISH-01", "HG-SPEND-01", "HG-DELETE-01", "HG-EXTERNAL-CONNECT-01",
  "HG-OWN-RECORDS-01", "HG-CONTEXT-MOVE-01", "HG-GOAL-CHANGE-01", "HG-SESSION-DISPATCH-01",
  "HG-WORLD-GOVERN-01", "HG-WORLD-GOVERN-02", "HG-PLATFORM-GOVERN-01", "HG-COMMERCE-OPERATE-01", "HG-COMMERCE-OPERATE-02", "HG-COMMERCE-PRICE-01", "HG-VOICE-LIVE-01", "HO-ACT-RECEIPT-01",
  "HG-BOARD-GOVERN-01", "HG-WORLD-BRAND-01", "HG-RUNTIME-UPDATE-01", "HG-PROCESSING-SCOPE-01", "HG-SELF-GOVERN-01",
  "HG-CODE-JOB-01", "HG-JOB-01", "HG-JOB-02", "HG-AGENT-ASK-01",
]);
const OPERATION_GATES = Object.freeze([
  operationGate("HG-COMM-SEND-01", "Cleo sends a message on your behalf", {
    mustShow: ["conversation-or-recipients", "exact-text", "World"],
  }),
  operationGate("HG-PUBLISH-01", "Cleo publishes or exposes content to an audience", {
    cardWaitMs: 300_000,
    mustShow: ["destination", "audience", "exact-content-or-revision", "public-address"],
  }),
  operationGate("HG-SPEND-01", "Cleo commits your money or credits", {
    mustShow: ["amount", "currency", "purpose-or-payee", "balance-after"],
  }),
  operationGate("HG-DELETE-01", "Cleo deletes, revokes, disables or disconnects something of yours", {
    mustShow: ["exact-item", "reversibility", "consequence"],
  }),
  operationGate("HG-EXTERNAL-CONNECT-01", "Connect, subscribe to or refresh an external account or feed", {
    kind: "HUMAN_INPUT", mustShow: ["provider", "address-or-scopes", "data-leaving"],
  }),
  operationGate("HG-OWN-RECORDS-01", "Change your own records and preferences in this World", {
    kind: "HUMAN_INPUT", mustShow: ["record", "old-value", "new-value"],
  }),
  operationGate("HG-CONTEXT-MOVE-01", "Change a session's context selection", {
    kind: "HUMAN_INPUT", cleoCanRef: "WSET", mustShow: ["members-added", "members-removed", "session", "reason"],
  }),
  operationGate("HG-GOAL-CHANGE-01", "Change the World's Goals, attempts or Ideas", {
    kind: "HUMAN_INPUT", mustShow: ["goal", "change", "revision"],
  }),
  operationGate("HG-SESSION-DISPATCH-01", "Open, retire or complete a room, session or execution", {
    kind: "HUMAN_INPUT", cleoCanRef: "CP1", mustShow: ["target", "engine", "parent"],
  }),
  operationGate("HG-WORLD-GOVERN-01", "Change this World's access, policy, offers or governance", {
    kind: "HUMAN_INPUT", deciderClass: "O", capability: "world.manage", mustShow: ["policy", "scope", "old-value", "new-value"],
  }),
  // Can 73 (docs/world-members-design.md §5.2; the 6 October exception under decision (f) of
  // docs/approval-gates-requirements.md): inviting, withdrawing, resending, revoking, restoring and removing a
  // World's people. A sibling of -01, as HG-COMMERCE-OPERATE-02 is of -01. HUMAN_IN_THE_LOOP and locked at "Ask me
  // each time" (AG15): decided only on the Members card bound to the exact stored change, never by a message, never by
  // Cleo, and nothing graduates it. userChangeable false also stops a founder making it stricter for themselves (AG6),
  // which the 4 October pause leaves for the new Cleo (§2.1, item 4).
  operationGate("HG-WORLD-GOVERN-02", "Change who belongs to this World", {
    cardWaitMs: 300_000,
    kind: "HUMAN_IN_THE_LOOP", deciderClass: "O", capability: "world.manage", humanDelegation: "not-delegable",
    worldChangeable: false, userChangeable: false, graduable: false,
    mustShow: ["person", "World", "old-standing", "new-standing", "seat-effect", "what-can-and-cannot-be-undone"],
  }),
  operationGate("HG-PLATFORM-GOVERN-01", "Change platform governance or law", {
    kind: "HUMAN_INPUT", deciderClass: "O", capability: "platform.govern", humanDelegation: "not-delegable",
    worldChangeable: false, userChangeable: false, graduable: false, mustShow: ["law-or-definition", "revision", "old-value", "new-value"],
  }),
  // Can 68, step 2 (the Commercial Operations plan, 5 October 2026: "On sale,
  // tiers, access and credits each ask on a card tied to the exact change,
  // whether from the Desk or from Cleo"). HUMAN_IN_THE_LOOP, no longer
  // HUMAN_INPUT: a message in the conversation is not the decision (the
  // W-89a78759 class), only the person's approval on the card bound to the
  // exact change is. The Desk board asks on its own card and records that
  // approval for the exact call (deskChangeApproval.ts).
  operationGate("HG-COMMERCE-OPERATE-01", "Change Neucleos commercial offers, pricing or credits", {
    kind: "HUMAN_IN_THE_LOOP", deciderClass: "O", capability: "commerce.operate", humanDelegation: "not-delegable",
    worldChangeable: false, userChangeable: false, graduable: false, mustShow: ["offer-or-credit", "target-World", "amount-or-price", "revision"],
  }),
  // Ending a World's paid subscription (docs/world-subscription-cancel-design.md).
  // Unlike -01 this is HUMAN_IN_THE_LOOP, never HUMAN_INPUT: the person's own
  // chat request is not the decision, a delegated standing cannot decide it,
  // and nothing graduates it. Only the person's click on the card, bound to
  // the exact call, satisfies it; the handler enforces that for every caller.
  operationGate("HG-COMMERCE-OPERATE-02", "Stop a World's paid subscription from renewing", {
    kind: "HUMAN_IN_THE_LOOP", deciderClass: "O", capability: "commerce.operate", humanDelegation: "not-delegable",
    worldChangeable: false, userChangeable: false, graduable: false,
    mustShow: ["target-World", "plan-and-amount", "period-end-date", "current-access", "effect", "reason"],
  }),
  // Can 68 (the Commercial Operations plan, 5 October 2026; the exception under decision (f) of
  // docs/approval-gates-requirements.md): changing what Neucleos charges members. A new gate arrives
  // locked at "Ask me each time" (AG15): decided on the card bound to the exact stored change, never by a
  // message, never by Cleo, and nothing graduates it.
  operationGate("HG-COMMERCE-PRICE-01", "Change what Neucleos charges for its models and services", {
    kind: "HUMAN_IN_THE_LOOP", deciderClass: "O", capability: "commerce.operate", humanDelegation: "not-delegable",
    worldChangeable: false, userChangeable: false, graduable: false,
    mustShow: ["every-new-price", "old-price", "approved-cost-and-markup", "what-else-changes"],
  }),
  // Can 81 (the coding-jobs plan, 17 October 2026; AG2, AG15): a coding job is a session that may change code in its own
  // copy of a repository, more than the conversation that asked for it (Can 22's one exception). It is its own gate,
  // never the session gate (HG-SESSION-DISPATCH-01, decided by a typed request): a planted instruction must get past the
  // person in plain words, so it is decided only on its card, bound to the exact brief, never by a message, never by
  // Cleo, and nothing graduates it. It arrives locked at "Ask me each time".
  operationGate("HG-CODE-JOB-01", "Start a coding job that changes a repository", {
    kind: "HUMAN_IN_THE_LOOP", deciderClass: "O", humanDelegation: "not-delegable",
    worldChangeable: false, userChangeable: false, graduable: false,
    mustShow: ["repository", "change", "brief", "time-limit", "where-it-runs", "how-it-lands"],
  }),
  // Can 52 (scheduled jobs; handoffs/orchestrators-2026-10-20/move/can52-design.md §3, §15-18): a job is a standing spend on the
  // person's money, so making or changing one is decided by the person on its card, never by a message (a cloud message carries no
  // authorship), never by Cleo, and nothing graduates it. Running one again (resume, run now, restore) is its own decision each time:
  // a Canvas button's press is not proof of a person (Codex review 4), and any turn's content could prompt Cleo to call it.
  operationGate("HG-JOB-01", "Run a request on a schedule", {
    kind: "HUMAN_IN_THE_LOOP", deciderClass: "O", humanDelegation: "not-delegable",
    worldChangeable: false, userChangeable: false, graduable: false,
    mustShow: ["request", "schedule", "time-zone", "who-pays", "where-it-runs"],
  }),
  operationGate("HG-JOB-02", "Run a scheduled job now or again", {
    kind: "HUMAN_IN_THE_LOOP", deciderClass: "O", humanDelegation: "not-delegable",
    worldChangeable: false, userChangeable: false, graduable: false,
    mustShow: ["job", "who-pays"],
  }),
  // Jason's decision 7, 22 Oct 2026 (docs/cleo-tool-system.md): a coding agent he connected (Claude Code, Codex) asks
  // him a question in his World (agentAsks.js). Only he answers it, in person: never delegated, never Cleo, never a
  // connector, never a request in a conversation; his choice and note are kept on the request for the agent to read.
  operationGate("HG-AGENT-ASK-01", "A coding agent asks you", {
    kind: "HUMAN_IN_THE_LOOP", deciderClass: "O", humanDelegation: "not-delegable",
    worldChangeable: false, userChangeable: false, graduable: false,
    mustShow: ["question", "choices", "agent"],
  }),
  operationGate("HG-VOICE-LIVE-01", "Start or price a Live voice call", {
    humanDelegation: "not-delegable", userChangeable: false, worldChangeable: false, graduable: false,
    cleoAugmentation: "quote-and-consent", mustShow: ["conversation", "price", "microphone-consent", "privacy-choice"],
  }),
  // Reviewer-proposed gates (2026-09-14 declaration review): the door's own
  // capability names whose table the gate appears in (D-GT5).
  // PR F (Q5, the coordinator's decision of 9 Oct 2026): a World's founder now decides who opens its Workrooms, so the
  // gate appears in founders' tables too (world.manage), and its standing only tightens (TIGHTEN_ONLY_GATES).
  operationGate("HG-BOARD-GOVERN-01", "Change this World's board access or profiles", {
    kind: "HUMAN_INPUT", deciderClass: "O", capability: "boards.manage", tableCapabilities: ["boards.manage", "world.manage"],
    mustShow: ["board-kind", "scope", "old-value", "new-value"],
  }),
  operationGate("HG-WORLD-BRAND-01", "Change this World's marks and brand", {
    kind: "HUMAN_INPUT", deciderClass: "O", capability: "world.brand", mustShow: ["mark", "old-value", "new-value", "cost"],
  }),
  operationGate("HG-RUNTIME-UPDATE-01", "Replace the Pocket cloud runtime", {
    mustShow: ["current-version", "target-version", "downtime", "rollback", "unknown-outcome-handling"],
  }),
  // WSET-CAN-12 (GOALS-WORKING-SET.html, "processing scope"): the founder's
  // declaration that the working-set judge may read members' records outside
  // any message boundary and send their words to an outside processor. The
  // judge reads this standing in the World and never an environment value.
  // Blocking by default: the judge has no person in its loop, so a kind that
  // waits for a person is the judge not running, and a scope nobody declared
  // leaves every proposal saying so. The founder's World act (world.manage,
  // class O) sets it on-the-loop: the judge runs and the proposal carries its
  // pins as the receipt. A member's own setting never widens a scope the
  // founder has not declared (userChangeable false), and Cleo cannot declare
  // it for anyone (not-delegable; the standing door refuses agents anyway).
  operationGate("HG-PROCESSING-SCOPE-01", "The working-set judge reads members' records under a declared scope", {
    deciderClass: "O", capability: "world.manage", humanDelegation: "not-delegable", cleoAugmentation: "prepare-preview",
    userChangeable: false, graduable: false, cleoCanRef: "WSET-CAN-12",
    mustShow: ["process", "what-it-reads", "where-it-sends", "cost-per-turn"],
  }),
  // The principal, 26 September 2026: Cleo may change her own rules (process
  // declarations, gate law, doctrine, her instructions, session tool
  // defaults, context rules) and decide approval requests when the person
  // explicitly tells her to. The person approves the exact change each time:
  // never delegable, never by a request alone, never loosened, so nothing Cleo
  // reads can change what she may do.
  operationGate("HG-SELF-GOVERN-01", "Cleo changes her own rules or decides an approval", {
    humanDelegation: "not-delegable", worldChangeable: false, userChangeable: false, graduable: false,
    mustShow: ["rule-or-approval", "old-value", "new-value", "who-asked"],
  }),
  operationGate("HO-ACT-RECEIPT-01", "Cleo acted on your behalf; receipt", {
    kind: "HUMAN_ON_THE_LOOP", timing: "AFTER_EFFECT", deciderClass: "N", defaultOutcome: "observed",
    humanDelegation: "informed", cleoAugmentation: "receipt", userChangeable: false, worldChangeable: false, graduable: false,
    allowedOutcomes: ["observed", "reverted"], satisfyingOutcomes: ["observed"], mustShow: ["exact-act", "who-decided", "effect-readback"],
  }),
]);

export const GATE_CATALOG = Object.freeze([
  ...OPERATION_GATES,
  settingsGate("MEDIA-PUBLISH-001", "Approve this publication", {
    capability: "media.approve", cleoCanRef: "MWA3", kind: "HUMAN_IN_THE_LOOP", timing: "BEFORE_EFFECT", humanDelegation: "not-delegable",
    mustShow: ["World", "destination-account-or-host", "public-address", "audience", "exact-content-preview", "revision", "schedule"],
    userChangeable: false, worldChangeable: false, graduable: false,
  }),
  toolGate("HG-TOOL-FILE-CHANGE-01", "Cleo changes a file", {}),
  toolGate("HG-TOOL-COMMAND-01", "Cleo runs a command", {}),
  toolGate("HG-TOOL-DYNAMIC-01", "Cleo calls a door or dynamic tool", {}),
  // Cleo Can 35 (decision 4 of the Cleo Architecture Reset, the principal,
  // 27 September 2026): outside the World's chosen folders Cleo asks once per
  // conversation, through request_folder_access.
  toolGate("HG-TOOL-FOLDER-ACCESS-01", "Cleo reads a folder outside your chosen folders", {}),
  // Decision 4: keys, passwords, keychains, browser profiles and message
  // databases need the person's approval even inside a chosen folder. Only the
  // person decides it, every time: not delegable, not graduable.
  toolGate("HG-TOOL-PROTECTED-ACCESS-01", "Cleo reads a place holding keys, passwords or private data", {
    humanDelegation: "not-delegable", graduable: false, userChangeable: false,
  }),
  toolGate("HO-TOOL-FILE-READ-01", "Cleo reads a file", {
    kind: "HUMAN_OBSERVATION", timing: "AFTER_EFFECT", deciderClass: "N", defaultOutcome: "observed",
    humanDelegation: "informed", allowedOutcomes: ["observed"], satisfyingOutcomes: ["observed"],
    userChangeable: false, graduable: false,
  }),
  // D-GT14 (the principal, 2026-09-03, relayed by the settings session):
  // "when I asked, I said the default should be set … we need a type of
  // approval that is based on a request. I shouldn't have to ask and then
  // approve myself." HUMAN_INPUT: the person's own request IS the decision;
  // Cleo's own initiative (no request) still awaits the person.
  settingsGate("HG-US-CHANGE-01", "Change a You / This Mac setting", {
    kind: "HUMAN_INPUT", cleoCanRef: "US-CAN-03", mustShow: ["setting-key", "lane", "old-value", "new-value"],
  }),
  // World-lane settings ride world.manage; the decider is the World's
  // operational owner (class O), never an attorney class.
  settingsGate("HG-US-CHANGE-02", "Change a World-lane setting", {
    kind: "HUMAN_INPUT", cleoCanRef: "US-CAN-04", capability: "world.manage", deciderClass: "O",
    mustShow: ["setting-key", "lane", "old-value", "new-value"], worldChangeable: true,
  }),
  settingsGate("HG-US-PROFILE-01", "Change your profile", {
    kind: "HUMAN_INPUT", cleoCanRef: "US-CAN-05", mustShow: ["field", "old-value", "new-value"],
  }),
  settingsGate("HO-US-CHANGE-01", "Settings change receipt", {
    kind: "HUMAN_ON_THE_LOOP", timing: "AFTER_EFFECT", deciderClass: "N", cleoCanRef: "US-CAN-03",
    defaultOutcome: "observed", humanDelegation: "informed", cleoAugmentation: "receipt",
    mustShow: ["setting-key", "lane", "old-value", "new-value", "receipt"],
    allowedOutcomes: ["observed", "reverted"], satisfyingOutcomes: ["observed"],
    userChangeable: false, worldChangeable: false, graduable: false,
  }),

  legalGate("LEGAL-HITL-001", "Route legal work", {
    deciderClass: "C", cleoCanRef: "BC-STAGE-01", capability: LEGAL_ROUTE_CAPABILITY,
    humanDelegation: "not-delegable", cleoAugmentation: "prepare-preview",
    mustShow: ["destination", "no-relationship-warning", "fields-and-files-leaving", "sensitivity-labels", "purpose", "retention-expectation", "envelope-digest"],
    allowedOutcomes: ["authorized", "cancelled"], satisfyingOutcomes: ["authorized"],
    worldChangeable: false, userChangeable: false,
  }),
  legalGate("LEGAL-HITL-002", "Conflict disposition", {
    deciderClass: "A", cleoCanRef: "BC-STAGE-03", capability: LEGAL_PRACTICE_CAPABILITY,
    humanDelegation: "attorney-only", cleoAugmentation: "surface-candidates",
    mustShow: ["candidate-matches", "relationship-roles", "source-recency", "uncertainty"],
    allowedOutcomes: ["clear", "decline", "investigate", "seek-consent"], satisfyingOutcomes: ["clear", "seek-consent"],
  }),
  legalGate("LEGAL-HITL-003", "Jurisdiction, competence, capacity, urgency", {
    deciderClass: "A", cleoCanRef: "BC-STAGE-04", capability: LEGAL_PRACTICE_CAPABILITY,
    humanDelegation: "attorney-only", cleoAugmentation: "triage",
    mustShow: ["requested-work", "governing-jurisdictions", "deadlines", "competence-capacity-evidence", "supervision-referral-options"],
    allowedOutcomes: ["accept", "narrow", "refer", "decline"], satisfyingOutcomes: ["accept", "narrow"],
  }),
  legalGate("LEGAL-HITL-004", "Representation and scope", {
    deciderClass: "A+C", cleoCanRef: "BC-STAGE-05", capability: LEGAL_ROUTE_CAPABILITY,
    humanDelegation: "not-delegable", cleoAugmentation: "draft-engagement",
    mustShow: ["client-identity", "scope-exclusions", "objectives", "responsibilities", "fees", "trust-treatment", "communications", "conflicts-consent"],
    allowedOutcomes: ["engaged", "non-engagement"], satisfyingOutcomes: ["engaged"],
    worldChangeable: true,
  }),
  legalGate("LEGAL-HITL-005", "Advice or material deliverable release", {
    deciderClass: "A", cleoCanRef: "BC-STAGE-07", capability: LEGAL_PRACTICE_CAPABILITY,
    humanDelegation: "attorney-only", cleoAugmentation: "draft-and-cite",
    mustShow: ["question", "facts-assumptions", "controlling-sources", "draft", "risk-rating"],
    allowedOutcomes: ["released", "revised", "withheld"], satisfyingOutcomes: ["released"],
  }),
  legalGate("LEGAL-HITL-006", "Representative act", {
    deciderClass: "A+C", cleoCanRef: "BC-STAGE-07", capability: LEGAL_PRACTICE_CAPABILITY,
    humanDelegation: "attorney-and-client-when-reserved", cleoAugmentation: "prepare-act",
    mustShow: ["exact-act-document", "authority", "deadline", "consequences", "affected-rights"],
    allowedOutcomes: ["approved", "declined"], satisfyingOutcomes: ["approved"],
  }),
  legalGate("LEGAL-HITL-007", "Client funds or property", {
    deciderClass: "D", cleoCanRef: "BC-STAGE-08", capability: LEGAL_PRACTICE_CAPABILITY,
    humanDelegation: "dual-control", cleoAugmentation: "reconcile-and-present",
    mustShow: ["owner", "ledger", "purpose", "cleared-status", "payee", "authorization", "balance-after"],
    allowedOutcomes: ["approved", "declined"], satisfyingOutcomes: ["approved"],
  }),
  legalGate("LEGAL-HITL-008", "Scope or party change", {
    deciderClass: "A+C", cleoCanRef: "BC-STAGE-08", capability: LEGAL_ROUTE_CAPABILITY,
    humanDelegation: "attorney-with-client-acceptance", cleoAugmentation: "diff-scope",
    mustShow: ["current-vs-proposed-scope", "conflicts-refresh", "deadlines", "fee-assumptions"],
    allowedOutcomes: ["accepted", "declined"], satisfyingOutcomes: ["accepted"],
    worldChangeable: true,
  }),
  legalGate("LEGAL-HITL-009", "Close or withdraw", {
    deciderClass: "A", cleoCanRef: "BC-STAGE-09", capability: LEGAL_PRACTICE_CAPABILITY,
    humanDelegation: "attorney-only", cleoAugmentation: "closing-checklist",
    mustShow: ["open-deadlines", "client-protection-steps", "final-deliverables", "funds-property", "bills", "file-transfer"],
    allowedOutcomes: ["approved", "declined"], satisfyingOutcomes: ["approved"],
  }),
  legalGate("LEGAL-HITL-010", "Collective contribution release", {
    deciderClass: "A", cleoCanRef: "BC-STAGE-10", capability: LEGAL_PRACTICE_CAPABILITY,
    humanDelegation: "attorney-plus-rights-holder", cleoAugmentation: "de-identify-and-verify-rights",
    mustShow: ["exact-proposed-bytes", "provenance", "client-matter-links", "de-identification", "rights"],
    allowedOutcomes: ["released", "rejected"], satisfyingOutcomes: ["released"],
  }),
]);

// Jason, 23 Oct 2026: “approval card wait, as you recommend”. Only the inline
// decision wait changes: publish and member-change cards get five minutes;
// every other gate keeps 45 seconds. Durable instance/credential expiry is separate.
export const DEFAULT_GATE_CARD_WAIT_MS = 45_000;
export const MAX_GATE_CARD_WAIT_MS = Math.max(DEFAULT_GATE_CARD_WAIT_MS,
  ...GATE_CATALOG.map((gate) => gate.cardWaitMs ?? DEFAULT_GATE_CARD_WAIT_MS));
export function gateCardWaitMs(gateKey) {
  return resolveGate(gateKey)?.cardWaitMs ?? DEFAULT_GATE_CARD_WAIT_MS;
}

export function resolveGate(gateKey) {
  return GATE_CATALOG.find((gate) => gate.gateKey === gateKey);
}

// AG11 (docs/approval-gates-requirements.md, approved by the principal; this
// line carried out on his "approved!", 4 October 2026; known hole 2 of
// docs/approval-gates-design.md): the locked platform lines. Every Cleo Code
// tool gate is World-changeable (toolGate above), so before this a founder
// could set Cleo's access to keys, passwords and private data to act and
// receipt. No World, member or personal setting, for the class or for any of
// its steps, goes looser than a line: a looser write is refused and a looser
// setting already recorded is read at the line, never rewritten
// (lib/gateStanding.js). Tightening stays allowed. Step 2 is "Ask me each
// time" (HUMAN_IN_THE_LOOP); step 1 (Touch ID or Face ID) is not a kind in
// this system yet, so the gate starts at step 2. The Desk holds the same line
// when the World answers (APPS/cleo-code/apps/server/src/neucleos/toolGates.ts).
export const LOCKED_PLATFORM_LINES = Object.freeze({
  "HG-TOOL-PROTECTED-ACCESS-01": Object.freeze({
    words: "Keys, passwords and credentials: starts at step 1, never looser than step 2",
    loosestKind: "HUMAN_IN_THE_LOOP",
  }),
});

/** The AG11 locked platform line that holds this gate, or null when none does. */
export function lockedPlatformLine(gateKey) {
  return Object.hasOwn(LOCKED_PLATFORM_LINES, gateKey) ? LOCKED_PLATFORM_LINES[gateKey] : null;
}

// Q5 (PR F; the coordinator's decision of 9 Oct 2026): gates whose standing may only be kept or tightened from their
// catalog kind, at every scope (World, member, the person's own, and each declared step). A looser setting is refused
// before anything is written, and one already recorded is read at the catalog kind, never rewritten
// (lib/gateStanding.js). This is NOT an AG11 locked line: it does not make the person decide every time, refuse a
// decision Cleo made under a delegation, or change what the Desk shows (the Desk reads only lockedPlatformLine).
export const TIGHTEN_ONLY_GATES = Object.freeze({
  "HG-BOARD-GOVERN-01": Object.freeze({
    words: "Who can open this World's Workrooms: asked each time it changes, never looser",
    loosestKind: "HUMAN_INPUT",
  }),
});

/** The tighten-only floor that holds this gate (Q5), or null when none does. */
export function tightenOnlyFloor(gateKey) {
  return Object.hasOwn(TIGHTEN_ONLY_GATES, gateKey) ? TIGHTEN_ONLY_GATES[gateKey] : null;
}

/** The capabilities whose holders see this gate in their table: its tableCapabilities, else its one capability. */
export const gateTableCapabilities = (entry) => entry?.tableCapabilities ?? (entry?.capability ? [entry.capability] : []);

// Structural decode against humanGate + the three estate fields. Returns the
// first defect as a string, or null. The catalog's own story re-validates
// every entry through this (derivation, never a hand-list — DL4).
export function validateGateEntry(entry) {
  if (!entry || typeof entry !== "object") return "entry is not an object";
  if (typeof entry.gateKey !== "string" || !entry.gateKey) return "gateKey missing";
  if (!GATE_KINDS.includes(entry.kind)) return `${entry.gateKey}: kind '${entry.kind}' outside ${GATE_KINDS.join("|")}`;
  if (!GATE_TIMINGS.includes(entry.timing)) return `${entry.gateKey}: timing outside ${GATE_TIMINGS.join("|")}`;
  if (entry.initialState !== "CREATED") return `${entry.gateKey}: initialState must be CREATED`;
  if (entry.cardWaitMs !== undefined && (!Number.isSafeInteger(entry.cardWaitMs) || entry.cardWaitMs <= 0)) return `${entry.gateKey}: cardWaitMs must be a positive safe integer`;
  if (typeof entry.defaultOutcome !== "string") return `${entry.gateKey}: defaultOutcome missing`;
  if (typeof entry.delegation?.humanDelegation !== "string" || typeof entry.delegation?.cleoAugmentation !== "string") return `${entry.gateKey}: delegation incomplete`;
  if (typeof entry.changeControl?.worldChangeable !== "boolean" || typeof entry.changeControl?.userChangeable !== "boolean") return `${entry.gateKey}: changeControl incomplete`;
  if (!entry.requiredView) return `${entry.gateKey}: requiredView missing`;
  if (!Array.isArray(entry.allowedOutcomes) || entry.allowedOutcomes.length === 0) return `${entry.gateKey}: allowedOutcomes empty`;
  if (!Array.isArray(entry.satisfyingOutcomes) || entry.satisfyingOutcomes.length === 0) return `${entry.gateKey}: satisfyingOutcomes empty`;
  for (const outcome of entry.satisfyingOutcomes) if (!entry.allowedOutcomes.includes(outcome)) return `${entry.gateKey}: satisfying outcome '${outcome}' not allowed`;
  if (!Array.isArray(entry.eligibleSurfaceBindings) || entry.eligibleSurfaceBindings.length === 0) return `${entry.gateKey}: eligibleSurfaceBindings empty`;
  if (!DECIDER_CLASSES.includes(entry.deciderClass)) return `${entry.gateKey}: deciderClass outside ${DECIDER_CLASSES.join("|")}`;
  if (typeof entry.cleoCanRef !== "string" || !entry.cleoCanRef) return `${entry.gateKey}: cleoCanRef missing`;
  if (typeof entry.capability !== "string" || !entry.capability) return `${entry.gateKey}: capability missing`;
  return null;
}

export function validateGateCatalog(catalog = GATE_CATALOG) {
  const seen = new Set();
  for (const entry of catalog) {
    const defect = validateGateEntry(entry);
    if (defect) return defect;
    if (seen.has(entry.gateKey)) return `duplicate gateKey ${entry.gateKey}`;
    seen.add(entry.gateKey);
  }
  return null;
}
